import { Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { CONTACT_EMAIL } from "../content";
import { track } from "../lib/analytics";

const ABOUT_PATHS = new Set(["/sobre-nosotros", "/sobre-nosotros/", "/about-us", "/about-us/"]);

function getInternalHref(href) {
  if (href.startsWith("/") && !href.startsWith("//")) return href;

  try {
    const url = new URL(href);
    const isScopelinkHost = url.hostname === "www.scopelink.io" || url.hostname === "scopelink.io";

    if (isScopelinkHost && ABOUT_PATHS.has(url.pathname)) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
  } catch {
    // Non-URL values are handled as external anchors below.
  }

  return null;
}

export default function Footer({ t }) {
  const handleContactClick = () => track("contact_click");

  return (
    <footer
      data-testid="footer"
      className="relative border-t border-white/10 bg-[#020407] pt-16 pb-8"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 mb-14">
          {/* Brand */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="relative inline-flex h-10 w-10 items-center justify-center border border-[var(--primary)]/40">
                <img
                  src="/images/isotipo_small_amarillo.png"
                  alt=""
                  aria-hidden="true"
                  className="h-5 w-5 object-contain"
                />
              </span>
              <div className="leading-none">
                <div className="font-display text-base font-extrabold">
                  SCOPELINK<span className="text-[var(--primary)]">.IO</span>
                </div>
                <div className="font-mono text-[9px] tracking-[0.3em] text-white/40 mt-1">
                  OBSERVATORY · IoT
                </div>
              </div>
            </div>
            <p className="text-white/55 text-sm leading-relaxed max-w-md">
              {t.footer.tag}
            </p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="footer-email"
              onClick={handleContactClick}
              className="mt-6 inline-flex items-center gap-3 font-mono text-xs tracking-[0.2em] uppercase text-white/70 hover:text-[var(--primary)] transition-colors"
            >
              <Mail className="h-4 w-4" />
              {CONTACT_EMAIL}
            </a>
          </div>

          {/* Quick links */}
          <div className="md:col-span-3">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--primary)] mb-5">
              {t.footer.colQuick}
            </div>
            <ul className="space-y-3">
              {t.footer.quickLinks.map((l, i) => (
                <li key={i}>
                  {getInternalHref(l.href) ? (
                    <Link
                      to={getInternalHref(l.href)}
                      data-testid={`footer-quick-${i}`}
                      className="text-sm text-white/60 hover:text-white transition-colors"
                    >
                      {l.label}
                    </Link>
                  ) : (
                    <a
                      href={l.href}
                      target={l.href.startsWith("http") ? "_blank" : undefined}
                      rel={l.href.startsWith("http") ? "noopener noreferrer" : undefined}
                      data-testid={`footer-quick-${i}`}
                      className="text-sm text-white/60 hover:text-white transition-colors"
                    >
                      {l.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div className="md:col-span-4">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--primary)] mb-5">
              {t.footer.colLegal}
            </div>
            <ul className="space-y-3">
              {t.footer.legalLinks.map((l, i) => (
                <li key={i}>
                  <a
                    href={l.href}
                    target={l.href.startsWith("http") ? "_blank" : undefined}
                    rel={l.href.startsWith("http") ? "noopener noreferrer" : undefined}
                    data-testid={`footer-legal-${i}`}
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="pt-6 border-t border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <p
            data-testid="footer-rights"
            className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40"
          >
            {t.footer.rights}
          </p>
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/30">
            STATUS · <span className="text-[var(--primary)]">ALL SYSTEMS NOMINAL</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
